import { useCallback, useEffect, useRef } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { Notify } from '~/components2/Notify';
import { useGeoItemDeepLink } from '../useGeoItemDeepLink';
import store from './store';

/**
 * Binds `?geoItem=` to the field-note detail viewer, and centres the map on a record arrived at
 * cold. Mounted only by the Field Notes tab, which owns the param — see FieldNotesLayer.
 *
 * Readiness is the load completing for this project, not the list being non-empty: a project with
 * no notes must still resolve the param, a failed load must not report the note missing, and the
 * previous project's notes must not answer for this one after a project switch.
 */
export function useFieldNoteDeepLink(projectId: string) {
  const { fieldNotesLoaded, fieldNotes, detailItem, projectId: loadedProjectId } = useSnapshot(store);
  // The tab renders in the side panel, outside <Map>, so the map has to be looked up by id.
  const { projectDetailsMap: map } = MapGL.useMap();

  // Only a record the URL opened gets the camera; a card or marker click leaves the viewport alone.
  const flyToId = useRef<string | null>(null);

  const has = useCallback((id: string) => store.fieldNotes.some((note) => note.id === id), []);

  const onOpen = useCallback((id: string) => {
    const item = store.fieldNotes.find((note) => note.id === id);
    if (!item) return;
    flyToId.current = id;
    store.openDetailItem(item, 'deeplink');
  }, []);

  const onClose = useCallback(() => store.setDetailItem(null), []);

  const onNotFound = useCallback(() => {
    Notify.error({
      title: 'Field Notes',
      message: 'That field note is not in this project, or you no longer have access to it.',
    });
  }, []);

  const openId = detailItem?.id ?? null;
  useGeoItemDeepLink({
    ready: fieldNotesLoaded && loadedProjectId === projectId,
    openId,
    has,
    onOpen,
    onClose,
    onNotFound,
  });

  // Keyed on the map instance, which is registered after this hook first runs on a cold load, and
  // on the coordinates rather than the list so a refresh does not move the camera again.
  const [lng, lat] = (openId && fieldNotes.find((note) => note.id === openId)?.geometry?.coordinates) || [];
  useEffect(() => {
    if (!map || !openId || flyToId.current !== openId) return;
    if (lng === undefined || lat === undefined) return;
    flyToId.current = null;
    map.flyTo({ center: { lng, lat }, duration: 1_000 });
  }, [map, openId, lng, lat]);
}
